import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ArrowRight, ChevronDown, ChevronUp, Star, BarChart3, Globe, MessageSquare, Search, Share2, Mail, Smartphone, Bot, Building2, FileText, MapPin } from 'lucide-react';
import { useState } from 'react';
import INDUSTRIES from '../data/industries';
import { STATES, METROS } from '../data/locations';
import SEOHead, { createBreadcrumbSchema, createServiceSchema, createFAQSchema, createOrganizationSchema } from './SEOHead';

const COLORS = { primary: '#002FA7', accent: '#FFCC00', dark: '#0A1628', light: '#F8FAFC' };
const TOOL_ICONS = {
  'Listings Management': Globe, 'Review Monitoring': Star, 'Local SEO': Search, 'Social Media': Share2,
  'Google Ads': BarChart3, 'SMS Marketing': Smartphone, 'Email Campaigns': Mail, 'AI Visibility': Bot,
  'Citation Building': Building2, 'Reputation Management': Star, 'Monthly Reports': FileText, 'Google Business Profile': MapPin,
};

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left" data-testid={`faq-toggle-${q.slice(0,20).replace(/\s/g,'-').toLowerCase()}`}>
        <span className="text-base font-medium text-slate-800 pr-8" style={{ fontFamily: "'Outfit', sans-serif" }}>{q}</span>
        {open ? <ChevronUp className="h-5 w-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />}
      </button>
      {open && <p className="pb-5 text-sm text-slate-600 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{a}</p>}
    </div>
  );
}

export default function IndustryLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const industry = INDUSTRIES.find(i => i.slug === slug);

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: COLORS.dark }}>
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('industry.industryNotFound')}</h1>
          <Link to="/" className="text-blue-400 hover:underline">{t('industry.backToHome')}</Link>
        </div>
      </div>
    );
  }

  const schema = [
    createOrganizationSchema(),
    createServiceSchema(industry),
    createFAQSchema(industry.faqs),
    createBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Industries', url: '/#industries' },
      { name: industry.name }
    ])
  ];

  const otherIndustries = INDUSTRIES.filter(i => i.slug !== slug).slice(0, 12);

  return (
    <>
      <SEOHead title={industry.metaTitle} description={industry.metaDescription} schema={schema} />

      <div className="min-h-screen" style={{ background: COLORS.light }}>
        {/* Nav */}
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: COLORS.primary }}>
                <span className="text-white font-bold text-sm">LB</span>
              </div>
              <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>LocalBranding Pro</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/#features" className="text-sm text-slate-600 hover:text-slate-900">{t('industry.features')}</Link>
              <Link to="/#pricing" className="text-sm text-slate-600 hover:text-slate-900">{t('industry.pricing')}</Link>
              <Link to="/blog" className="text-sm text-slate-600 hover:text-slate-900">{t('industry.industryNews')}</Link>
              <Link to="/admin/login" className="text-sm font-medium" style={{ color: COLORS.primary }}>{t('industry.logIn')}</Link>
              <Link to="/admin/login" className="px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ background: COLORS.primary }} data-testid="nav-cta">
                {t('industry.startFreeTrial')}
              </Link>
            </div>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-3">
          <nav className="flex text-xs text-slate-500" data-testid="breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-400">Industries</span>
            <span className="mx-2">/</span>
            <span style={{ color: COLORS.primary }} className="font-medium">{industry.name}</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="industry-hero">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] mb-4 inline-block" style={{ color: COLORS.primary }}>
                {t('industry.builtFor', { industry: industry.name })}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
                {industry.headline}
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                {industry.subtext}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/admin/login" className="px-6 py-3 rounded-full text-sm font-semibold text-white flex items-center gap-2 transition-transform hover:scale-105" style={{ background: COLORS.primary }} data-testid="hero-cta">
                  {t('industry.startFreeTrial')} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="flex gap-6 mt-6 text-xs text-slate-500">
                <span>{t('industry.freeTrial14')}</span><span>{t('industry.noCreditCardShort')}</span><span>{t('industry.cancelAnytime')}</span>
              </div>
            </div>
            <div className="relative">
              <img src={industry.image} alt={industry.imageAlt} className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]" loading="eager" />
            </div>
          </div>
        </section>

        {/* Pain Points */}
        <section className="py-16" style={{ background: '#f1f5f9' }}>
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: COLORS.primary }}>{t('industry.soundFamiliar')}</p>
            <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              {t('industry.commonChallenges', { industry: industry.name.toLowerCase() })}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {industry.painPoints.map((p, i) => (
                <div key={i} className="bg-white rounded-xl p-6 flex gap-4 shadow-sm" data-testid={`pain-point-${i}`}>
                  <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: COLORS.primary }}>{i + 1}</div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solutions */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: COLORS.primary }}>The solution</p>
            <h2 className="text-3xl font-bold text-center mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              How LocalBranding Pro helps {industry.name.toLowerCase()} businesses
            </h2>
            <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Everything you need to dominate local search and grow your {industry.name.toLowerCase()} business.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.solutions.map((s, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`solution-${i}`}>
                  <h3 className="font-semibold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16" style={{ background: COLORS.dark }}>
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3 text-center" style={{ color: COLORS.accent }}>{t('industry.useCases')}</p>
            <h2 className="text-3xl font-bold text-center mb-12 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t('industry.perfectFor', { industry: industry.name.toLowerCase() })}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {industry.useCases.map((uc, i) => (
                <div key={i} className="flex items-center gap-3 bg-white/5 rounded-lg p-4 border border-white/10">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: COLORS.accent }} />
                  <span className="text-sm text-white/90" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{uc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16" style={{ background: '#f1f5f9' }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <p className="text-xl md:text-2xl italic text-slate-700 mb-4 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              "{industry.testimonial.text}"
            </p>
            <p className="text-sm font-medium text-slate-500">— {industry.testimonial.author}</p>
          </div>
        </section>

        {/* Tools Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              {t('industry.allTools', { industry: industry.name.toLowerCase() })}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {industry.tools.map((tool, i) => {
                const Icon = TOOL_ICONS[tool] || Globe;
                return (
                  <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-4 border border-slate-100 shadow-sm" data-testid={`tool-${i}`}>
                    <Icon className="h-5 w-5" style={{ color: COLORS.primary }} />
                    <span className="text-sm font-medium text-slate-700" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{tool}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16" style={{ background: '#f1f5f9' }}>
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              {t('industry.faqTitle', { industry: industry.name.toLowerCase() })}
            </h2>
            <div className="bg-white rounded-xl shadow-sm divide-y divide-slate-100 px-6">
              {industry.faqs.map((faq, i) => <FAQItem key={i} q={faq.q} a={faq.a} />)}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.dark }}>
              {t('industry.readyToGrow', { industry: industry.name.toLowerCase() })}
            </h2>
            <p className="text-slate-600 mb-8" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {t('industry.joinLocal')}
            </p>
            <Link to="/admin/login" className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-semibold text-white transition-transform hover:scale-105" style={{ background: COLORS.primary }} data-testid="bottom-cta">
              {t('industry.startFreeTrial')} <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="text-xs text-slate-500 mt-3">{t('industry.noCreditCard')}</p>
          </div>
        </section>

        {/* Browse by Location */}
        <section className="py-12 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-sm font-semibold text-slate-500 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t('industry.marketingByState', { industry: industry.name })}
            </h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {STATES.map(st => (
                <Link key={st.slug} to={`/industries/${slug}/${st.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700 transition-colors">
                  {st.name}
                </Link>
              ))}
            </div>
            <h3 className="text-sm font-semibold text-slate-500 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t('industry.marketingByCity', { industry: industry.name })}
            </h3>
            <div className="flex flex-wrap gap-2">
              {METROS.slice(0, 30).map(m => (
                <Link key={m.slug} to={`/industries/${slug}/${m.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700 transition-colors">
                  {m.full}
                </Link>
              ))}
              {METROS.length > 30 && <span className="text-xs text-slate-400 self-center">{t('industry.moreCities', { count: METROS.length - 30 })}</span>}
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-12 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-sm font-semibold text-slate-500 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('industry.alsoBuiltFor')}</h3>
            <div className="flex flex-wrap gap-2">
              {otherIndustries.map(ind => (
                <Link key={ind.slug} to={`/industries/${ind.slug}`} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700 transition-colors" data-testid={`crosslink-${ind.slug}`}>
                  {ind.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-500">&copy; {new Date().getFullYear()} LocalBranding Pro. {t('industry.allRightsReserved')}</p>
            <p className="text-xs text-slate-500">A <a href="https://web-techservices.com" className="text-slate-600 hover:text-slate-800 transition-colors">Web-TechServices AI</a> product.</p>
            <div className="flex gap-4 text-xs text-slate-500">
              <Link to="/" className="hover:text-slate-700">{t('industry.home')}</Link>
              <Link to="/blog" className="hover:text-slate-700">{t('industry.industryNews')}</Link>
              <Link to="/admin/login" className="hover:text-slate-700">{t('industry.adminLogin')}</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
